import type { StateClassKey } from "@tdelivr/design-tokens";

import type { BoardRow } from "./board.ts";

/**
 * What the SLOs and telemetry board does to the one listing it reads, away from any rendering.
 *
 * Built to `16 WP15 Platform Machinery.dc.html`, page `slo`, against one operation — and one
 * decision not to publish a second:
 *
 *   - OBS-02 `GET /v1/platform/slos` —
 *     [ADR 0066](../../../docs/adr/0066-a-platform-scoped-slo-listing-publishes-the-budget-as-a-ratio-and-withholds-its-terms.md),
 *     which is OBS-02's **first public business operation and its first collection read of any
 *     kind**: its four internal operations are a define, a revise, a by-identifier read and an
 *     evaluation write, and the repository's only definition read is `latest(tenantId, sloId)`,
 *     which needs the `sloId` a board exists to discover;
 *   - OBS-01 `telemetry-gateway` —
 *     [ADR 0065](../../../docs/adr/0065-the-telemetry-gateways-counters-are-not-published-because-they-are-one-replicas.md),
 *     which **declines to publish anything**, with reasons. See below.
 *
 * ```
 * slo:['SLOs and telemetry','OBS-01 · OBS-02',
 *   'Service objectives with the error budget left, not just uptime.',
 *   [['SLOs','64','done','met'],['Burning fast','2','failed','budget'],
 *    ['At risk','5','caution','this month'],['Alerts open','3','human','']],
 *   ['Service','Objective','Budget left'],
 *   [['Payment orchestrator','99.9% under 800 ms','12%','failed','burning fast'],
 *    ['Dispatch','99.5% offers in 3 s','61%','done','healthy'],
 *    ['Driver location','99% fresh under 30 s','34%','caution','at risk']],
 *   'warn','A burning budget pages the on-call team; the console only shows it.']
 * ```
 *
 * ## The telemetry half of the title has no source, by decision
 *
 * `navigation.ts` names two owners for this page and the design's source chip writes both:
 * `OBS-01 · OBS-02`. **OBS-01 publishes nothing for it and will not**, and ADR 0065 is that
 * decision rather than a gap waiting on route work. Three findings in it, any one sufficient:
 *
 *   - **OBS-01 holds no database.** There is no `postgres-repository.ts` and no `migrate.ts` in its
 *     `src/`; its `service.yaml` declares `data_ownership: [Ephemeral bounded streaming buffers and
 *     duplicate-batch window]`. Its whole state is `GatewayStats` — four integers (`buffered`,
 *     `accepted`, `dropped`, `exportFailures`) that are instance fields of one `TelemetryGateway`
 *     object, in a service whose declared `scaling_profile` is a horizontal regional gateway. A
 *     console calling a public address would be answered by whichever pod the load balancer picked,
 *     so a figure would change between two refreshes without anything having happened.
 *   - **The counters carry no tenant at all.** `accept()` merges every tenant's items into one
 *     queue, and every column of this board is per tenant.
 *   - **Nothing designed needs it.** The four tiles and three columns above contain no throughput
 *     figure, no buffered-items figure and no export-failure figure. OBS-01 is in the design's own
 *     service line for this page and the designed screen draws nothing it could answer.
 *
 * So this board reads one service, draws no telemetry element, and says so under the grid. The
 * source chip still writes both codes, because that is the design's copy and `CLAUDE.md` §1 governs
 * it. ADR 0065 §4 asks for `navigation.ts`'s absence clause to be withdrawn and replaced with that
 * statement, because an absence recorded against something the design does not draw inflates the
 * measurement of how much of the design is unbuildable.
 *
 * ## The page stays degraded with ADR 0066 served, and that is expected
 *
 * Two of the four tiles and one of the three badge states are **unmodelled**, not unpublished, and
 * ADR 0066's closing section measured each:
 *
 *   - **`burning fast`, which is the design's first row's badge, and `Burning fast · 2 · budget`.**
 *     OBS-02 computes **no burn rate**: `evaluateBudget` derives permitted, consumed, remaining and
 *     `complete`, and `burn` appears nowhere in that service's `src/`. A rate needs two evaluations
 *     or the elapsed fraction of the window, and §4 withholds the window bounds *with* the counts,
 *     so it is not client-derivable either. The board draws a two-state badge and omits the third.
 *   - **`Alerts open · 3`.** `service.yaml` puts `alert delivery` under `non_responsibilities`, so
 *     no service in this cohort holds an alert at all. This one is further from being served than
 *     the burn rate: it is not a field anybody is withholding.
 *
 * ## The budget is a ratio and its terms are not published, by decision
 *
 * `remainingBudgetPermille` arrives as a remaining permille **with no counts behind it**. That is
 * the withholding ADR 0066 is named for: `slo_evaluations` holds `goodEvents`, `totalEvents`,
 * `unknownEvents`, `permittedBadEvents`, `consumedBadEvents` and `remainingBadEvents` over a named
 * window, and those six numbers over that window are one operator's request volume — which on a
 * ride-hailing and delivery platform is its order volume, and the tenants of this platform compete.
 * The design draws `12%`, and a percentage is publishable without its denominator.
 *
 * The window bounds are withheld **with** the counts rather than separately, because a window plus a
 * consumed count is a rate and a rate reconstructs the volume. So there is nothing on this board to
 * divide, and `windowDays` — which *is* published — is the objective's own declared window, a
 * configuration value the operator chose rather than a measurement of its traffic.
 *
 * Three readings of that one field are the service's decisions and this board honours each:
 *
 *   - **`null` is not zero.** It means the objective has never been evaluated, or the newest
 *     evaluation permitted zero bad events. Zero per mille means "no budget left"; a board drawing
 *     `null` as zero would report an untested objective as a failing one. Drawn as the design's own
 *     `—`, never as `0%`.
 *   - **Zero is real and is clamped there.** `remainingBadEvents` goes negative once an objective is
 *     overspent, by an amount proportional to the overspend, and the service clamps at zero — which
 *     publishes "no budget left" and withholds how far past zero the operator went.
 *   - **`complete` says the percentage is provisional.** It is `unknownEvents === 0`, and it carries
 *     that information without carrying the count. Drawn in the panel.
 *
 * ## What the listing publishes, and what it deliberately does not
 *
 * Eleven fields: `tenantId`, `sloId`, `serviceId`, `name`, `targetNumerator`, `targetDenominator`,
 * `windowDays`, `version`, `remainingBudgetPermille`, `evaluatedAt`, `complete`. Withheld, each for
 * ADR 0066 §4's reason: **the six event counts** and **both window bounds** — see above;
 * **`telemetrySourceReference`, `sliDefinitionReference`, `exclusionPolicyReference`** — URNs
 * pointing into one operator's telemetry evidence and policy documents, and a list of another
 * tenant's evidence identifiers is a list of things to go and ask for; **`ownerTeam`** — internal
 * team names, which across every tenant on one page is an org chart; **`createdBy`** — an identity
 * subject; **`evaluationId`** — it names a row of a listing this service does not publish; and
 * **`active`**, withheld for the opposite reason to all of the above: not a secret but a
 * **constant**, since `createDefinition` returns `active: true` unconditionally and the only write
 * against `slo_definitions` is one `INSERT`. A column fed by a constant cannot inform a board.
 *
 * ## The cells nothing can fill
 *
 * **The `Service` column's prose.** The design writes `Payment orchestrator`, `Dispatch`,
 * `Driver location`. `serviceId` matches `/^[A-Z]{2,5}-\d{2}$/`, so the stored value is `PAY-01`,
 * and **nothing in OBS-02 holds a service display name** — its own `PlatformSloSummary` comment
 * says `serviceId` "holds no human display name". The board draws the code, in Geist Mono, and says
 * so: a service registry would have to publish the mapping, and none does. This is the published
 * identifier in place of a name that `board.ts`'s `mono` exists for (Q-102's case).
 *
 * **The `Objective` column's measure half.** `99.9% under 800 ms` is two facts. The percentage is
 * exactly computable from `targetNumerator / targetDenominator`; `under 800 ms` would have to come
 * from `sliDefinitionReference`, which is a `urn:tdelivr:sli:…` rather than prose — and is withheld
 * in any case. The designed form collects `Measure (text)` and the contract parses a URN, so the
 * form's field and the contract's field are different kinds of value. The cell draws the half it can
 * prove rather than `—`, because the percentage is the half the column is named for and is right.
 *
 * **The detail panel's `History` names and instants.** `Created · 12 Aug 2026 · W. Mutua` and
 * `Last changed · today 09:12 · K. Otieno` are literals in the design file. `createdAt` is not in
 * this projection, there is no `updatedAt` to withhold — a revision inserts a new `version` row and
 * nothing updates a prior one — and the subjects are withheld by §4. Q-107.
 *
 * **The KPI tile row, which is not drawn on any board of this console.** `SLOs · 64` is a count no
 * listing publishes a total for, and the other three are the absences above. Q-178.
 */

/**
 * The scope OBS-02's platform SLO listing is bought with.
 *
 * A literal in this application's source, which is what grants it: `channel-scopes.mjs` derives
 * `tdelivr-platform-console`'s realm grant from the scope strings this console's own files name, so
 * naming it here is the grant and nothing else is. ADR 0032's mechanism, ADR 0049's rule that a
 * channel names only the scopes its drawn screens spend, and ADR 0066 §2 on why `slo:read` is not
 * widened instead — it buys every field of a named objective, including the owner team, three URNs
 * and the creating subject, and granting that to a browser in order to draw three columns is
 * ADR 0025's rejected alternative re-taken.
 *
 * Not `slo:any`: `AGENTS.md` reserves `:any` for an escalation over a *subject*, and this is an
 * escalation over a *tenancy*. Not `observability:platform:read`, which would start a second
 * vocabulary root under a domain rather than a resource.
 *
 * The literal lives in `navigation.ts`, where all eighteen of this console's cross-boundary scopes
 * are collected so that "what can this console reach" is answerable from one file. Re-exported here
 * so a reader of this module finds it beside the reasoning, and so `channel-scopes.mjs` sees one
 * literal rather than two.
 *
 * **Q-139 takes a further occasion here**, and this one reads every tenant's reliability posture as
 * a percentage.
 */
export { SLO_SCOPE } from "./navigation.ts";

/**
 * One service objective, as OBS-02's platform listing publishes it.
 *
 * The eleven fields of `PlatformSloSummary`, restated here rather than imported from the generated
 * client for one reason: the generated **client method** types `remainingBudgetPermille`,
 * `evaluatedAt`, `complete` and `nextAfter` as `unknown`, because the contract writes their
 * nullability as `type: [number, "null"]` and the generator does not narrow that form.
 * `ui/SloTelemetry.tsx` narrows each by `typeof` at the edge, and everything below this line works
 * on a type that says what the contract says.
 */
export interface Slo {
  readonly tenantId: string;
  readonly sloId: string;
  /** The service code, `PAY-01`. OBS-02 holds no human display name for it. */
  readonly serviceId: string;
  readonly name: string;
  readonly targetNumerator: number;
  readonly targetDenominator: number;
  /** The objective's own declared window: 28 or 30, the only two values the define accepts. */
  readonly windowDays: number;
  readonly version: number;
  /** The error budget remaining in parts per thousand, clamped `0 … 1000`, or `null`. */
  readonly remainingBudgetPermille: number | null;
  readonly evaluatedAt: string | null;
  readonly complete: boolean | null;
}

/**
 * A ratio as the design writes a percentage: `99.9%`, `99.5%`, `99%`, `12%`.
 *
 * Trailing zeros are trimmed rather than padded, which is what makes all four of those forms come
 * out of one function — the design writes `99%` and not `99.0%`, and `99.9%` and not `100%`. The
 * value is **not** rounded to a whole number: `615` per mille is `61.5%`, because rounding it to
 * `61%` would be the board deciding that half a per mille of an error budget is noise, which is a
 * judgement about another operator's incident. Four decimal places is the cut, which is more than
 * either input can carry — `targetDenominator` is at most 1 000 000 and the permille is an integer.
 */
export function percentText(value: number): string {
  return `${String(Number(value.toFixed(4)))}%`;
}

/**
 * The design's `Objective` column, as much of it as is published: `99.9%`.
 *
 * `targetNumerator / targetDenominator`, which the service validates as `1 ≤ numerator < denominator
 * ≤ 1 000 000` — so the quotient is always a proper fraction and the percentage is always below 100.
 * The design's `under 800 ms` half has no source; see this file's header.
 */
export function objectiveText(slo: Slo): string {
  if (slo.targetDenominator === 0) return "—";
  return percentText((slo.targetNumerator / slo.targetDenominator) * 100);
}

/**
 * The design's `Budget left` column: `12%`, `61%`, `34%`.
 *
 * A remaining permille read as a percentage, which is a division by ten and nothing else. `null`
 * answers `undefined` here and the cell draws the design's own `—`: see this file's header on why
 * `null` is not zero.
 */
export function budgetText(slo: Slo): string | undefined {
  if (slo.remainingBudgetPermille === null) return undefined;
  return percentText(slo.remainingBudgetPermille / 10);
}

/**
 * Where this board puts the line between `healthy` and `at risk`, in parts per thousand.
 *
 * **The design states no threshold, so the board picks one and this is the record that it picked
 * it** — ADR 0066's closing section says exactly that. Five hundred per mille is half the budget,
 * which is the one defensible round number available, and it reproduces two of the design's three
 * rows: `61%` is `healthy` above it and `34%` is `at risk` below it. Its first row, `12%`, is
 * `burning fast` in the design and is `at risk` here, because a burn rate is what distinguishes the
 * two and nothing computes one.
 *
 * It is a constant in the console's source rather than a published field on purpose: a threshold
 * that arrived from the service would be a server rule, and a board recomputing a server rule is
 * `event-replays.ts`'s finding. There is no server rule here to restate.
 */
export const AT_RISK_PERMILLE = 500;

/**
 * The badge's class and word, from the remaining budget alone.
 *
 * Two of the design's three words are its own and the third is never drawn:
 *
 * | budget                    | class     | word          | whose word                            |
 * | ------------------------- | --------- | ------------- | ------------------------------------- |
 * | `> 500` per mille         | `done`    | `healthy`     | the design's second row               |
 * | `≤ 500` per mille         | `caution` | `at risk`     | the design's third row                |
 * | `null`                    | `unknown` | `not measured`| this board's, and the record says so  |
 * | a burn rate               | `failed`  | `burning fast`| **never drawn** — nothing computes it |
 *
 * `not measured` is the one word on this board that is neither the design's nor a contract's, and
 * it is needed because the design has no row for an objective nobody has evaluated. The `unknown`
 * class is the token system's own for a state that "borrows neither success nor failure, because
 * neither is true yet", which is exactly this: a `caution` badge would report an untested objective
 * as one at risk, and a `done` badge would report it as met.
 *
 * A state is never shown by colour alone: all three carry a glyph and a word, and the three words
 * are three different words.
 */
export function stateOf(slo: Slo): { classKey: StateClassKey; label: string; title?: string } {
  if (slo.remainingBudgetPermille === null) {
    return {
      classKey: "unknown",
      label: "not measured",
      title:
        "No error budget has been published for this objective: either it has never been evaluated, or its newest evaluation permitted zero bad events. A ratio with a zero denominator is not zero, so this is not drawn as no budget left. The design has no row for this state; the word is this board's. ADR 0066 §5.",
    };
  }
  if (slo.remainingBudgetPermille > AT_RISK_PERMILLE) {
    return {
      classKey: "done",
      label: "healthy",
      title: `More than half the error budget remains. The design's third badge — burning fast — is never drawn on this board: OBS-02 computes no burn rate, and the window bounds that would let a client derive one are withheld with the event counts. The ${String(AT_RISK_PERMILLE)} per mille line is this board's own, because the design states none. ADR 0066.`,
    };
  }
  return {
    classKey: "caution",
    label: "at risk",
    title: `Half the error budget or less remains. The design's third badge — burning fast — is never drawn on this board: OBS-02 computes no burn rate, and the window bounds that would let a client derive one are withheld with the event counts. The ${String(AT_RISK_PERMILLE)} per mille line is this board's own, because the design states none. ADR 0066.`,
  };
}

/** The states this reading can reach, worst first, which is the design's own badge sort order. */
export const SLO_STATES: readonly string[] = ["at risk", "not measured", "healthy"];

/** The states present on the rows the board is holding, so the filter offers nothing empty. */
export function statesPresent(slos: readonly Slo[]): readonly string[] {
  const present = new Set(slos.map((slo) => stateOf(slo).label));
  return SLO_STATES.filter((label) => present.has(label));
}

/**
 * An instant as the design's two time grammars compose it: `1 Sep 2026 09:12`.
 *
 * Both halves are the design's — `1 Sep 2026` is every date cell in these files and `09:12` is the
 * `History` block's `Last changed`. A day alone would lose the time an error budget was measured at,
 * which on an objective evaluated through a day is the difference between two readings; a clock
 * alone would not say which day. UTC, because every instant on these boards is published as UTC and
 * the design names no zone anywhere (Q-24).
 */
const clock = new Intl.DateTimeFormat("en-GB", {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: "UTC",
});

export function asInstant(instant: string): string {
  const parsed = Date.parse(instant);
  if (!Number.isFinite(parsed)) return instant;
  const at = new Date(parsed);
  const month = new Intl.DateTimeFormat("en-GB", { month: "short", timeZone: "UTC" })
    .format(at)
    .slice(0, 3);
  return `${String(at.getUTCDate())} ${month} ${String(at.getUTCFullYear())} ${clock.format(at)}`;
}

/** One row of the SLOs and telemetry board. */
export function sloRow(slo: Slo): BoardRow {
  const state = stateOf(slo);
  const budget = budgetText(slo);

  return {
    /*
     * The tenant and the objective: OBS-02's primary key and this listing's cursor. `sloId` is
     * server-minted and so is unique platform-wide, but the key is still the pair, because the
     * primary key is the pair and a cursor should be a prefix of the `ORDER BY` — ADR 0066 §6.
     */
    id: `${slo.tenantId}:${slo.sloId}`,
    cells: [
      {
        text: slo.serviceId,
        /*
         * Mono, which is `board.ts`'s extension of the design's own rule: a published identifier a
         * page is drawing in place of a name. The design's grid takes Geist Mono for a value matching
         * its prefix list — `ten_`, `evt_`, `RPL`, `RSK`, `wf_`, `/v1`, `key`, `offset` — and writes
         * `Payment orchestrator` in Geist here, because it expected prose. A service code set in the
         * body font would read as the display name it is not.
         */
        mono: true,
        title:
          "The service code, which is what OBS-02 stores. The design writes a display name — Payment orchestrator — and nothing in this service holds one: serviceId matches ^[A-Z]{2,5}-\\d{2}$. A service registry would have to publish the mapping. ADR 0066.",
      },
      {
        text: objectiveText(slo),
        title:
          "The target, computed from the published numerator and denominator. The design's second half — under 800 ms — has no source: it would come from sliDefinitionReference, which is a URN rather than prose and is withheld with the telemetry evidence identifiers. ADR 0066 §4.",
      },
      budget === undefined
        ? {
            text: "",
            absent: true,
            title:
              "No error budget is published for this objective: it has never been evaluated, or its newest evaluation permitted zero bad events. Not drawn as 0%, which would report an untested objective as a failing one. ADR 0066 §5.",
          }
        : {
            text: budget,
            title:
              slo.complete === false
                ? "The remaining error budget, as a ratio with no counts behind it — the six event counts over the window are one operator's request volume, and a window plus a count is a rate that reconstructs it. This evaluation was incomplete (some events were unknown), so the percentage is provisional. Clamped at zero, so an overspent objective reads as no budget left rather than publishing how far past zero it went. ADR 0066 §4 and §5."
                : "The remaining error budget, as a ratio with no counts behind it — the six event counts over the window are one operator's request volume, and a window plus a count is a rate that reconstructs it. Clamped at zero, so an overspent objective reads as no budget left rather than publishing how far past zero it went. ADR 0066 §4 and §5.",
          },
    ],
    state,
    /*
     * No row tint. The design tints its `human` and `failed` classes, and neither is reachable here
     * — `burning fast` is the `failed` row and nothing computes a burn rate. `event-catalogue.ts`
     * records that this console's stylesheet carries `--humanRow` and not `--failRow`.
     */
    detail: [
      { label: "Tenant", value: slo.tenantId, mono: true },
      { label: "Objective", value: slo.sloId, mono: true },
      /*
       * `name` is published and the design has no column for it, which is the panel's own purpose.
       * The grid's first column is the service code, so without this line an objective's own name —
       * the three-to-120 characters an operator wrote — appears nowhere on the page.
       */
      { label: "Name", value: slo.name },
      /*
       * The design's own grammar for this value, from its own form's `Window` select:
       * `7 days | 28 days | 30 days`. The contract accepts 28 or 30, which is the first half of why
       * that form cannot be sent.
       */
      { label: "Window", value: `${String(slo.windowDays)} days` },
      slo.evaluatedAt === null
        ? { label: "Budget evaluated", value: "", absent: true }
        : { label: "Budget evaluated", value: asInstant(slo.evaluatedAt) },
      /*
       * `complete` is `unknownEvents === 0`, published beside the ratio so an operator can see that
       * the percentage is provisional — it carries that information without carrying the count. It is
       * `null` when there is no evaluation, which is the same absence the budget cell draws.
       */
      slo.complete === null
        ? { label: "Evaluation complete", value: "", absent: true }
        : { label: "Evaluation complete", value: slo.complete ? "yes" : "no" },
    ],
    /*
     * The design's `History` block with the one entry an objective genuinely has.
     *
     * That block reads `Created`, `Last changed` and `Version` on every page of every one of these
     * design files, with a person's name beside the first two. This projection publishes **neither
     * instant**: `createdAt` is not in it, and there is no `updatedAt` to withhold — a revision
     * inserts a new `version` row and nothing updates or deletes a prior one, which is also what
     * makes this listing's cursor immutable. `createdBy` is withheld as an identity subject
     * (ADR 0066 §4), so the design's two names have no source in any case. Q-107.
     */
    history: [
      { label: "Created", value: "", absent: true },
      { label: "Last changed", value: "", absent: true },
      { label: "Version", value: `v${String(slo.version)}` },
    ],
    /*
     * The design's own glyph for this panel, which is its `FORMS.slo` icon: `monitoring`. Its panel
     * head renders `fm.icon`, and `fm.icon` comes from the page's entry in `FORMS`.
     */
    glyph: "monitoring",
  };
}

/**
 * What the board owes an operator about the one listing behind it and the one that declined.
 *
 * Four things, and each is said rather than implied because each invites a stronger reading than the
 * data supports:
 *
 *   - **how many tenants the rows came from**, because the page is a board of every service
 *     objective on the platform and cannot show that it is: nothing publishes how many tenants exist
 *     — `POST /v1/tenants` exists and there is no `GET`.
 *   - **that the `burning fast` badge is never drawn and why**, because its absence is invisible: a
 *     board showing only `healthy` and `at risk` looks like a board on which nothing is burning.
 *     This is the one sentence on this page an operator would be misled without.
 *   - **where the board put the healthy line**, because the design states none and the threshold is
 *     therefore the console's own opinion rather than a reading.
 *   - **that OBS-01 publishes nothing by decision**, because it is in the page's own source chip and
 *     in its title, and silence there reads as a service that failed to answer.
 *
 * @param slos the objectives on the board, or `undefined` before they have arrived.
 */
export function listingNote(slos: readonly Slo[] | undefined): string | undefined {
  if (slos === undefined) return undefined;
  const tenants = new Set(slos.map((slo) => slo.tenantId)).size;
  if (tenants === 0) return undefined;

  return [
    `Service objectives from ${String(tenants)} tenant${tenants === 1 ? "" : "s"}.`,
    `At risk is drawn at ${percentText(AT_RISK_PERMILLE / 10)} of the budget or less, a line this board picked because the design states none.`,
    "The Service column is the service code, because nothing publishes a display name for it.",
  ].join(" ");
}

/**
 * Why the designed `Add a service objective` form is drawn and refused.
 *
 * Three reasons, and any one is enough. The form's fields are `Service`, `Target`, `Measure`,
 * `Window` and a `Page on-call when burning fast` toggle, and its primary is `Add SLO`:
 *
 *   - **`Window` offers `7 days | 28 days | 30 days` and the contract accepts `28 | 30`.** A write
 *     sending `7` would be refused, and a form that silently dropped the option would be redrawing
 *     the design.
 *   - **`Page on-call when burning fast` has no field in either `SloDefinition` or the route's
 *     schema**, and the burn rate it names is the thing this service does not compute. A write would
 *     have to invent two fields, which `CLAUDE.md` §2 forbids.
 *   - **No write is published under `/v1/platform` at all.** ADR 0066 §9, and OBS-02's
 *     `tests/security/platform-listing.test.ts` asserts the router's 404 on three platform-shaped
 *     writes with the platform scope held — so "not published" is measured rather than described.
 *
 * The word on the button is the design's own `FORMS.slo[0]`: `Add SLO`.
 */
export const ADD_REFUSAL =
  "Not available. The form's Window select offers 7 days, which the contract refuses, and its Page on-call when burning fast toggle has no field in OBS-02 — the burn rate it names is not computed anywhere. No write is published under /v1/platform. ADR 0066 §9.";
